import React, { useState, useEffect, useRef } from 'react';
import { CrackAnalysis, Severity, InspectionMode, ImageResolutionMode, IMAGE_RESOLUTION_OPTIONS } from '../types';
import { analyzeStaticSurface } from '../services/claudeService';
import { saveFileToProject } from '../services/fileSystemService';
import { LoadingOverlay } from './LoadingOverlay';

// ... interfaces ...
interface ImageInspectorProps {
  initialFiles: File[];
  onComplete: (timeline: CrackAnalysis[]) => void;
  onCancel: () => void;
  toolSpecs?: string;
  initialModes?: InspectionMode[];
}

const PROTOCOL_OPTIONS = [
  { id: InspectionMode.CRACK_ONLY, label: 'Crack Detection', icon: '⚡', color: 'border-orange-500', bg: 'bg-orange-50', activeBg: 'bg-orange-100', desc: 'Cracks & deep scratches.' },
  { id: InspectionMode.WELD_INTEGRITY, label: 'Weld Integrity', icon: '🛡️', color: 'border-emerald-500', bg: 'bg-emerald-50', activeBg: 'bg-emerald-100', desc: 'Junctions & HAZ audit.' },
  { id: InspectionMode.GENERAL_NDT, label: 'Structural Audit', icon: '🏗️', color: 'border-blue-500', bg: 'bg-slate-300', activeBg: 'bg-slate-400', desc: 'Cracks, fissures, corrosion & dents.' },
  { id: InspectionMode.FOULING_INSPECTION, label: 'Cleanliness', icon: '🧪', color: 'border-purple-500', bg: 'bg-purple-50', activeBg: 'bg-purple-100', desc: 'Residue & Scaling.' }
];

export const ImageInspector: React.FC<ImageInspectorProps> = ({
  initialFiles,
  onComplete,
  onCancel,
  toolSpecs,
  initialModes = [InspectionMode.GENERAL_NDT]
}) => {
  // ... (Keep existing hooks: useState, useEffect, useRef) ...
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [activeModes, setActiveModes] = useState<InspectionMode[]>(initialModes);
  const [sensitivity, setSensitivity] = useState(5);
  const [resolutionMode, setResolutionMode] = useState<ImageResolutionMode>(ImageResolutionMode.STANDARD);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(false);
  const [isAborting, setIsAborting] = useState(false);
  const stopNeuralEngine = () => { abortRef.current = true; setIsAborting(true); };
  const [statusLog, setStatusLog] = useState<string[]>(["STATUS: IMAGES LOADED. AWAITING CONFIGURATION."]);
  const [isArchiving, setIsArchiving] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let isMounted = true;
    const loadPreviews = async () => {
      const loaded = await Promise.all(files.map(f => new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(f);
      })));
      if (isMounted) {
        setPreviews(loaded);
        if (currentImageIndex >= loaded.length) setCurrentImageIndex(Math.max(0, loaded.length - 1));
      }
    };
    if (files.length > 0) loadPreviews();
    else if (isMounted) setPreviews([]);
    return () => { isMounted = false; };
  }, [files]);

  const toggleMode = (mode: InspectionMode) => {
    setActiveModes(prev => {
      if (prev.includes(mode)) {
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== mode);
      }
      return [...prev, mode];
    });
  };

  const handleAddImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = (Array.from(e.target.files) as File[]).filter(f => f.type.startsWith('image/'));
      const uniqueFiles = newFiles.filter(nf => !files.some(ef => ef.name === nf.name && ef.size === nf.size));
      if (uniqueFiles.length > 0) {
        setIsArchiving(true);
        try {
          // Auto-save to Local Project
          for (const f of uniqueFiles) {
            await saveFileToProject('01_Evidence_Images', f.name, f);
          }
          setFiles(prev => [...prev, ...uniqueFiles]);
          setStatusLog(prev => [...prev, `ADDED & ARCHIVED ${uniqueFiles.length} NEW FILES.`]);
        } finally {
          setIsArchiving(false);
        }
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };



  const runBatchAnalysis = async () => {
    if (previews.length === 0 || files.length !== previews.length) {
      alert("System synchronizing assets. Please wait a moment.");
      return;
    }
    abortRef.current = false;
    setIsAborting(false);
    setIsProcessing(true);
    setProgress(0);
    setStatusLog(prev => [...prev, ">>> INITIALIZING BATCH NEURAL AUDIT..."]);
    const batchTimeline: CrackAnalysis[] = [];
    const total = previews.length;
    for (let i = 0; i < total; i++) {
      if (abortRef.current) break;
      let base64 = previews[i];
      const file = files[i];
      const filename = file.name;
      setCurrentImageIndex(i);
      setStatusLog(prev => [...prev, `Analyzing Frame ${i + 1}/${total}: ${filename}...`].slice(-6));

      // Resize image to respect resolution mode for storage and result display
      const targetRes = IMAGE_RESOLUTION_OPTIONS.find(o => o.mode === resolutionMode) || IMAGE_RESOLUTION_OPTIONS[0];
      const resizedBase64 = await new Promise<string>((resolve) => {
        const img = new Image();
        img.src = base64;
        img.onload = () => {
          if (img.width <= targetRes.px && img.height <= targetRes.px) {
            resolve(base64);
            return;
          }
          const ratio = Math.min(targetRes.px / img.width, targetRes.px / img.height);
          const w = Math.floor(img.width * ratio);
          const h = Math.floor(img.height * ratio);
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.9));
          } else resolve(base64);
        };
        img.onerror = () => resolve(base64);
      });
      base64 = resizedBase64;

      // Auto-save original evidence to Local Project if active
      await saveFileToProject('01_Evidence_Images', filename, file);

      try {
        const result = await analyzeStaticSurface(base64, activeModes, toolSpecs, sensitivity, 5000, 'gemini-2.5-pro', '', undefined, resolutionMode);
        const enriched = { ...result, frameImage: base64, videoName: filename, timestamp: i };
        batchTimeline.push(enriched);
      } catch (err) {
        console.error(err);
        batchTimeline.push({
          detected: false,
          severity: Severity.NONE,
          mode: activeModes[0],
          description: `Analysis failed for ${filename}`,
          recommendation: "Retry individual scan",
          confidenceScore: 0,
          frameImage: base64,
          videoName: filename,
          timestamp: i,
          findings: [],
          diagnosticNote: "Processing error."
        } as any);
      }
      setProgress(Math.round(((i + 1) / total) * 100));
      await new Promise(r => setTimeout(r, 200));
    }
    if (abortRef.current) {
      setStatusLog(prev => [...prev, "AUDIT ABORTED. COMPILING RESULTS..."]);
    } else {
      setStatusLog(prev => [...prev, "AUDIT COMPLETE. COMPILING RESULTS..."]);
    }
    await new Promise(r => setTimeout(r, 500));
    setIsProcessing(false);
    setIsAborting(false);
    onComplete(batchTimeline);
  };

  const pendingPreviews = previews.slice(currentImageIndex + 1);
  const processedCount = currentImageIndex;
  const isReady = !isProcessing && files.length > 0 && files.length === previews.length;

  return (
    <div className="flex flex-col h-full bg-slate-300">
      <input type="file" ref={fileInputRef} multiple accept="image/*" className="hidden" onChange={handleAddImages} />
      <div className="p-4 border-b border-slate-500 flex justify-between items-center bg-slate-400 shadow-sm">
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-none ${isProcessing ? 'bg-blue-600 animate-pulse' : 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]'}`}></div>
            <h3 className="font-black text-slate-900 uppercase tracking-tighter text-base italic leading-none">GEA Image Survey</h3>
            <span className="text-[9px] text-[#0000a9] font-black uppercase tracking-widest bg-slate-300 px-2 py-0.5 ml-2 border border-blue-100">V5.2</span>
          </div>
        </div>
        <button onClick={onCancel} disabled={isProcessing} className="text-slate-700 hover:text-red-600 transition uppercase text-[10px] font-black tracking-widest bg-slate-300 px-4 py-2 border border-slate-400 disabled:opacity-50 shadow-sm rounded-none">Abort Mission</button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-6 bg-slate-300 relative">
        <div className="flex w-full h-full gap-6">
          <div className="flex-[3] relative flex items-center justify-center overflow-hidden bg-slate-300/50 backdrop-blur-sm border border-slate-500 shadow-2xl group rounded-none">
            {previews.length > 0 ? (
              <>
                <img
                  src={previews[currentImageIndex]}
                  className={`max-h-full max-w-full shadow-2xl border-4 border-slate-500 transition-opacity duration-300 ${isProcessing ? 'opacity-80' : 'opacity-100'}`}
                  alt="Audit Target"
                />
                {files.length > 1 && !isProcessing && (
                  <div className="absolute inset-x-0 bottom-8 flex justify-center items-center gap-4 pointer-events-none">
                    <button onClick={() => setCurrentImageIndex(i => Math.max(0, i - 1))} className="pointer-events-auto p-3 bg-slate-300 shadow-xl border border-slate-400 hover:text-[#0000a9] transition disabled:opacity-30 rounded-none text-slate-700" disabled={currentImageIndex === 0}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="bg-[#0000a9] shadow-xl px-6 py-2 border border-[#0000a9] text-[10px] font-black uppercase tracking-widest text-white">
                      {currentImageIndex + 1} / {files.length}
                    </span>
                    <button onClick={() => setCurrentImageIndex(i => Math.min(files.length - 1, i + 1))} className="pointer-events-auto p-3 bg-slate-300 shadow-xl border border-slate-400 hover:text-[#0000a9] transition disabled:opacity-30 rounded-none text-slate-700" disabled={currentImageIndex === files.length - 1}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                )}
                {!isProcessing && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute top-8 right-8 pointer-events-auto bg-[#0000a9] hover:bg-[#0000c0] shadow-xl text-white px-6 py-3 text-[10px] font-black uppercase tracking-widest transition flex items-center gap-3 opacity-0 group-hover:opacity-100 rounded-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                    Add More Assets
                  </button>
                )}
                {isProcessing && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="scan-line-beta-1 opacity-20"></div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-6">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-24 h-24 border-2 border-dashed border-slate-400 bg-slate-300 flex items-center justify-center hover:border-[#0000a9] hover:text-[#0000a9] transition-all group shadow-sm rounded-none"
                >
                  <svg className="w-10 h-10 text-slate-800 group-hover:text-[#0000a9]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                </button>
                <div className="text-slate-700 uppercase font-black tracking-[0.2em] text-xs">Awaiting Asset Payload</div>
              </div>
            )}
            {isProcessing && (
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between pointer-events-none gap-6">
                <div className="bg-slate-300/95 backdrop-blur-md border border-slate-400 p-6 shadow-2xl text-left pointer-events-auto w-72 rounded-none">
                  <span className="text-[9px] font-black text-slate-700 uppercase tracking-widest block mb-1">Audit Stream Status</span>
                  <span className="text-2xl font-black italic text-slate-900 leading-none">{progress}% COMPLETE</span>
                  <div className="w-full h-1 bg-slate-400 overflow-hidden mt-4">
                    <div className="h-full bg-[#0000a9] transition-all duration-300 shadow-[0_0_10px_rgba(0,0,169,0.3)]" style={{ width: `${progress}%` }}></div>
                  </div>
                </div>
                <div className="h-32 flex-1 bg-slate-300/95 backdrop-blur-md border border-slate-400 p-5 flex flex-col shadow-2xl text-left pointer-events-auto overflow-hidden rounded-none">
                  <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 font-mono text-[9px] text-left">
                    {statusLog.map((log, i) => (
                      <div key={i} className="text-slate-700 border-l-2 border-slate-400 pl-3 uppercase">{log}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex-1 relative bg-slate-400 border border-slate-500 overflow-hidden shadow-2xl flex flex-col p-6 rounded-none">
            <div className="flex justify-between items-center mb-6 border-b border-slate-500 pb-3">
              <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest leading-none">Detection Queue</h4>
              <span className="text-[9px] bg-slate-300 text-slate-700 border border-slate-400 px-3 py-1 font-bold rounded-none uppercase">{pendingPreviews.length} Remaining</span>
            </div>
            {pendingPreviews.length > 0 ? (
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                <div className="grid grid-cols-2 gap-4">
                  {pendingPreviews.map((src, i) => (
                    <div key={i} className="aspect-square relative overflow-hidden border border-slate-500 group bg-slate-300 rounded-none shadow-sm">
                      <img src={src} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" alt="Queue Item" />
                      <div className="absolute top-2 left-2 bg-slate-300/90 backdrop-blur px-2 py-1 text-[8px] font-black text-slate-900 border border-slate-500 shadow-sm uppercase">
                        #{processedCount + i + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center opacity-40">
                <div className="w-16 h-16 border-2 border-dashed border-slate-500 flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                </div>
                <span className="text-slate-700 text-[10px] uppercase font-black tracking-widest italic">Operations Complete</span>
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-slate-500 grid grid-cols-2 gap-4">
              <div className="bg-slate-300 p-4 border border-slate-400 rounded-none shadow-sm">
                <span className="text-[9px] text-slate-700 font-black uppercase block mb-1">Audit Ledger Size</span>
                <span className="text-base font-mono font-black text-slate-900">{files.length} ITEMS</span>
              </div>
              <div className="bg-slate-300 p-4 border border-slate-400 rounded-none shadow-sm">
                <span className="text-[9px] text-slate-700 font-black uppercase block mb-1">Status Vector</span>
                <span className="text-base font-mono font-black text-emerald-600">{processedCount} OK</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 bg-slate-400 border-t border-slate-500 space-y-10 overflow-y-auto max-h-[50vh] custom-scrollbar text-left">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic leading-none">Global Mission Protocol</h4>
            <div className="grid grid-cols-2 gap-4 w-full">
              {PROTOCOL_OPTIONS.map(protocol => {
                const isActive = activeModes.includes(protocol.id);
                return (
                  <button
                    key={protocol.id}
                    onClick={() => !isProcessing && toggleMode(protocol.id)}
                    disabled={isProcessing}
                    className={`relative group p-5 border text-left transition-all overflow-hidden rounded-none shadow-sm ${isActive ? `${protocol.color} ${protocol.bg}` : 'bg-slate-300 border-slate-400 hover:border-slate-500'}`}
                  >
                    <div className="flex flex-col h-full justify-between gap-4 relative z-10">
                      <div className="flex justify-between items-start">
                        <span className={`text-2xl ${isActive ? 'scale-110' : 'grayscale opacity-30 transition-all duration-500'}`}>{protocol.icon}</span>
                        {isActive && <div className="w-2 h-2 bg-[#0000a9] animate-pulse rounded-none"></div>}
                      </div>
                      <div>
                        <h3 className={`font-black text-[11px] uppercase tracking-widest mb-1 ${isActive ? 'text-[#0000a9]' : 'text-slate-900'}`}>
                          {protocol.label}
                        </h3>
                        <p className={`text-[9px] font-bold leading-tight uppercase tracking-tight ${isActive ? 'text-[#0000a9]/70' : 'text-slate-800'}`}>
                          {protocol.desc}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-8">
            <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic leading-none">Technical Parameters</h4>
            <div className="space-y-8 pt-2">
              <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"><span className="text-slate-700">Audit Sensitivity</span><span className="text-[#0000a9]">LVL {sensitivity} / 10</span></div>
              <input type="range" min="1" max="10" step="1" value={sensitivity} onChange={(e) => setSensitivity(parseInt(e.target.value))} disabled={isProcessing} className="w-full h-1 bg-white rounded-none appearance-none cursor-pointer accent-[#0000a9] disabled:opacity-50" />

              <div className="space-y-3 pt-2">
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-700">Neural Resolution</div>
                <div className="flex flex-col gap-2">
                  {IMAGE_RESOLUTION_OPTIONS.map(opt => (
                    <button
                      key={opt.mode}
                      onClick={() => !isProcessing && setResolutionMode(opt.mode)}
                      disabled={isProcessing}
                      className={`w-full text-left px-4 py-2.5 border transition-all flex justify-between items-center rounded-none shadow-sm ${resolutionMode === opt.mode
                        ? 'bg-[#0000a9] border-[#0000a9] text-white'
                        : 'bg-slate-300 border-slate-400 text-slate-800 hover:border-[#0000a9]/60'
                        } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-[10px] font-black uppercase tracking-widest">{opt.label}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-tight ${resolutionMode === opt.mode ? 'text-white/70' : 'text-slate-600'}`}>{opt.desc.split('·').pop()?.trim()}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-300 p-6 border border-slate-400 mt-4 flex items-center justify-between shadow-sm rounded-none">
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center text-[10px] font-black text-slate-700 uppercase tracking-widest">
                  <span>Batch Payload</span>
                  <span className="text-slate-900 ml-6">{files.length} FILES</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black text-slate-700 uppercase tracking-widest">
                  <span>Audit Estimate</span>
                  <span className="text-slate-900 ml-6">~{files.length * 2} SEC</span>
                </div>
              </div>
              <button
                disabled={isProcessing}
                onClick={() => fileInputRef.current?.click()}
                className="px-6 py-3 bg-slate-400 border border-slate-400 text-slate-800 hover:border-[#0000a9] text-[9px] font-black uppercase tracking-widest transition disabled:opacity-50 shadow-sm rounded-none"
              >
                + Payload Add
              </button>
            </div>
          </div>
        </div>

        <button onClick={isProcessing ? stopNeuralEngine : runBatchAnalysis} disabled={(isProcessing && isAborting) || (!isProcessing && (!isReady || activeModes.length === 0))} className={`w-full py-8 font-black uppercase text-[14px] tracking-[0.4em] shadow-2xl transition-all active:scale-[0.99] italic rounded-none border-b-4 ${isProcessing ? (isAborting ? 'bg-red-900 text-white/50 cursor-not-allowed border-red-950' : 'bg-red-600 hover:bg-red-500 text-white animate-pulse border-red-700') : 'bg-[#0000a9] hover:bg-[#0000c0] disabled:bg-slate-300 disabled:text-slate-300 disabled:border-slate-400 text-white border-blue-900 shadow-[0_20px_40px_rgba(0,0,169,0.2)]'}`}>
          {isProcessing ? (isAborting ? 'HALTING NEURAL ENGINE...' : 'STOP NEURAL ENGINE') : 'START NEURAL AUDIT'}
        </button>
      </div>
      {isArchiving && <LoadingOverlay message="ARCHIVING STATIC ASSETS" subMessage="COMMITTING HIGH-RES IMAGES TO PROJECT VAULT" />}
    </div>
  );
};